/**
 * Link bundles: the links going from one group to another, drawn as **one** thick link with a
 * count instead of many crossing lines.
 *
 * A link is bundled at the level where its two nodes part ways: below the deepest group they
 * share, each side is represented by the group (or the node) it sits in at that level. So a
 * link from a node in Backend › Modules to a node in Libraries › Rust crates goes in the bundle
 * "Backend → Libraries", while a link between Modules and Foundation (both in Backend) goes in
 * "Modules → Foundation". Links inside one group are never bundled.
 *
 * Pure functions on plain data, shared by the scene (drawing) and the inspector (listing).
 */
import type { Graph } from "@protocol/Graph";
import type { Link } from "@protocol/Link";

/** One end of a bundle: `g:<group id>` or `n:<node id>`. */
export type BundleEnd = string;

/** A bundle: every link from one end to the other (in that direction). */
export interface Bundle<L> {
  /** `<from>><to>`, stable while both ends exist. */
  key: string;
  from: BundleEnd;
  to: BundleEnd;
  links: L[];
}

/** The fewest links a bundle needs; a single link is drawn as itself. */
export const MIN_BUNDLE = 2;

/** The end standing for a group. */
export const groupEnd = (id: string): BundleEnd => `g:${id}`;

/** The end standing for a node. */
export const nodeEnd = (id: string): BundleEnd => `n:${id}`;

/** What an end stands for. */
export function parseEnd(end: BundleEnd): { kind: "group" | "node"; id: string } {
  return { kind: end.startsWith("g:") ? "group" : "node", id: end.slice(2) };
}

/** The key of the bundle from `from` to `to`. */
export const bundleKey = (from: BundleEnd, to: BundleEnd): string => `${from}>${to}`;

/**
 * The groups a node sits in, outermost first (`[]` when it is in no group). Stops on a group
 * seen twice, so a broken tree cannot loop.
 */
export function groupChain(group: string | null, parentOf: (group: string) => string | null): string[] {
  const chain: string[] = [];
  for (let g: string | null = group; g && !chain.includes(g); g = parentOf(g)) chain.unshift(g);
  return chain;
}

/**
 * Gathers `links` into bundles of at least {@link MIN_BUNDLE} links. `ends` gives a link's two
 * node ids, `groupOf` a node's group, `parentOf` a group's parent. Links that stay inside one
 * group, or whose bundle would be too small, are not in any bundle.
 */
export function bundleLinks<L>(
  links: L[],
  ends: (link: L) => { from: string; to: string },
  groupOf: (node: string) => string | null,
  parentOf: (group: string) => string | null,
): Bundle<L>[] {
  const chains = new Map<string, string[]>();
  /** A node's group chain, computed once. */
  const chainOf = (node: string) => {
    let chain = chains.get(node);
    if (!chain) chains.set(node, (chain = groupChain(groupOf(node), parentOf)));
    return chain;
  };
  const byKey = new Map<string, Bundle<L>>();
  for (const link of links) {
    const { from: start, to: end } = ends(link);
    const a = chainOf(start);
    const b = chainOf(end);
    let shared = 0;
    while (shared < a.length && shared < b.length && a[shared] === b[shared]) shared++;
    const from = shared < a.length ? groupEnd(a[shared]!) : nodeEnd(start);
    const to = shared < b.length ? groupEnd(b[shared]!) : nodeEnd(end);
    if (from.startsWith("n:") && to.startsWith("n:")) continue; // same group: drawn as it is
    const key = bundleKey(from, to);
    let bundle = byKey.get(key);
    if (!bundle) byKey.set(key, (bundle = { key, from, to, links: [] }));
    bundle.links.push(link);
  }
  return [...byKey.values()].filter((b) => b.links.length >= MIN_BUNDLE);
}

/** The name shown for an end: the group's or the node's name (its id if it is gone). */
export function endName(graph: Graph, end: BundleEnd): string {
  const { kind, id } = parseEnd(end);
  const found = kind === "group" ? graph.groups.find((g) => g.id === id) : graph.nodes.find((n) => n.id === id);
  return found?.name ?? id;
}

/**
 * The bundle `key` of a graph, from the links `shown` keeps (the same filters as the canvas),
 * or `undefined` when it does not exist (any more).
 */
export function bundleIn(graph: Graph, key: string, shown: (link: Link) => boolean): Bundle<Link> | undefined {
  const groups = new Map(graph.groups.map((g) => [g.id, g]));
  const nodes = new Map(graph.nodes.map((n) => [n.id, n]));
  return bundleLinks(
    graph.links.filter(shown),
    (l) => l,
    (id) => {
      const g = nodes.get(id)?.group;
      return g && groups.has(g) ? g : null;
    },
    (id) => {
      const parent = groups.get(id)?.parent;
      return parent && groups.has(parent) ? parent : null;
    },
  ).find((b) => b.key === key);
}
